import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Editor } from "react-draft-wysiwyg";
import { ContentState, convertToRaw, EditorState } from "draft-js";
import { Button, Card, Col, Container, Form, InputGroup, Row } from "react-bootstrap";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import {
  applicationTypeEnum,
  GetKeyByValue,
  GetValueByKey,
  imageTypeEnum,
} from "../../../../../Helpers/EnumHelper";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import ApiHelper from "../../../../../Helpers/ApiHelper";
import ScrollableCard from "../../../../SharedComponents/ScrollableCard";
import { useLocation, useNavigate } from "react-router-dom";
import BreadCrumb from "../../../BreadCrumb";
import { useDispatch, useSelector } from "react-redux";
import { deleteInfoImage } from "../Store/Thunk";
import ImageUploadField from "./ImageUploadField";
import { useImageUpload } from "../Hooks/useImageUpload";
import { ACCEPTED_IMAGE_TYPES, DEFAULT_EDITOR_HTML, IMAGE_DIMENSIONS, MAX_FILE_SIZE } from "../Constants";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

export default function ApplicationInfo() {
  const { state } = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const application = state;
  const user = useSelector((state) => state.auth.user);

  // Form fields state
  const [infoTitle, setInfoTitle] = useState(application?.info?.infoTitle || "");
  const [description, setDescription] = useState(application?.info?.description || "");
  const [editorState, setEditorState] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Image upload states using custom hook
  const iconImage = useImageUpload();
  const pdfCoverImage = useImageUpload();
  const pdfInfoImage = useImageUpload();

  useEffect(() => {
    initializeEditorState(application?.info?.infoHtml);
  }, [application?.info?.infoHtml, application?.info?.infoTitle]);

  const initializeEditorState = (infoHtml) => {
    const htmlContent = infoHtml || DEFAULT_EDITOR_HTML;
    const contentBlock = htmlToDraft(htmlContent);
    const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
    setEditorState(EditorState.createWithContent(contentState));
  };

  const handleEditorChange = (state) => {
    setEditorState(state);
  };

  const getApplicationTypeName = () => {
    const typeKey = GetKeyByValue(application.applicationType, applicationTypeEnum);
    switch (typeKey) {
      case "Test":
        return t("test") || "Test";
      case "Inventory":
        return t("inventory") || "Envanter";
      case "Survey":
        return t("survey") || "Anket";
      case "Assessment":
        return t("assessment") || "Değerleme";
      default:
        return typeKey;
    }
  };

  const handleDeleteImage = (imageType, imageName, imageHook) => {
    const typeEnum = GetValueByKey(imageType, imageTypeEnum);
    dispatch(deleteInfoImage({ type: typeEnum, imageName: imageName, infoId: application.info.id }));
    imageHook.handleDelete();
  };

  const updateApplicationInfo = () => {
    setIsSaving(true);

    const formData = new FormData();

    formData.append("pdfCoverImage", pdfCoverImage.imageState.file);
    formData.append("pdfInfoImage", pdfInfoImage.imageState.file);
    formData.append("iconImage", iconImage.imageState.file);
    formData.append("infoHtml", draftToHtml(convertToRaw(editorState?.getCurrentContent())));
    formData.append("applicationId", application?.id);
    formData.append("infoTitle", infoTitle);
    formData.append("description", description);
    formData.append("applicationType", application.applicationType);

    const onSuccess = (res) => {
      setIsSaving(false);
      const response = JSON.parse(res.response);

      if (response.statusCode === 1) {
        MySwal.fire({
          title: <strong>{t("successAlertHeading") || "Başarılı"}</strong>,
          html: <i>{t("infoUpdatedSuccessfully") || "Bilgiler başarıyla güncellendi"}</i>,
          icon: "success",
          confirmButtonText: t("ok") || "Tamam",
          confirmButtonColor: "#0d6efd",
        }).then(() => {
          // Navigate back to the application list after user closes the alert
          navigate(`/${GetKeyByValue(application.applicationType, applicationTypeEnum)}`);
        });
      } else {
        MySwal.fire({
          title: <strong>{t("serverError") || "Hata"}</strong>,
          html: <i>{response.message || t("updateFailed") || "Güncelleme başarısız"}</i>,
          icon: "error",
          confirmButtonText: t("ok") || "Tamam",
          confirmButtonColor: "#dc3545",
        });
      }
    };

    const onError = () => {
      setIsSaving(false);
      MySwal.fire({
        title: <strong>{t("serverError") || "Hata"}</strong>,
        html: <i>{t("updateFailed") || "Güncelleme sırasında bir hata oluştu"}</i>,
        icon: "error",
        confirmButtonText: t("ok") || "Tamam",
        confirmButtonColor: "#dc3545",
      });
    };

    ApiHelper.xmlPostFormData("application/UpsertApplicationInfo", formData, user.token, onSuccess, onError);
  };

  return (
    <div>
      <BreadCrumb
        pageName={t("details")}
        backPageName={GetKeyByValue(application.applicationType, applicationTypeEnum)}
        backUrl={`/${GetKeyByValue(application.applicationType, applicationTypeEnum)}`}
      >
        <Row className="align-items-center">
          <Col xs="12" md="8">
            <h3 className="mb-0">
              {getApplicationTypeName()} {t("name")}: <strong>{application.name}</strong>
            </h3>
          </Col>
          <Col xs="12" md="4" className="text-end mt-3 mt-md-0">
            <Button
              variant="primary"
              onClick={updateApplicationInfo}
              disabled={isSaving}
              size="lg"
              style={{ minWidth: "150px" }}
            >
              {isSaving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {t("saving") || "Kaydediliyor..."}
                </>
              ) : (
                t("save")
              )}
            </Button>
          </Col>
        </Row>
      </BreadCrumb>

      <Card body className="shadow-sm">
        <ScrollableCard>
          <Container>
            {/* Icon Image Section */}
            <div className="mb-5">
              <h5 className="mb-3 pb-2 border-bottom text-primary">
                <i className="bi bi-image me-2"></i>
                {t("applicationIcon") || "Uygulama İkonu"}
              </h5>
              <Card className="p-3 bg-light">
                <Row>
                  <Col>
                    <ImageUploadField
                      label="Icon"
                      imageState={iconImage.imageState}
                      existingImageName={application?.info?.iconImageName}
                      onFileSelect={iconImage.handleFileSelect}
                      onDelete={() => handleDeleteImage("ApplicationIcon", application?.info?.iconImageName, iconImage)}
                      dimensions={IMAGE_DIMENSIONS.icon}
                      maxFileSize={MAX_FILE_SIZE}
                      accept={ACCEPTED_IMAGE_TYPES}
                    />
                  </Col>
                </Row>
              </Card>
            </div>

            {/* PDF Images Section */}
            <div className="mb-5">
              <h5 className="mb-3 pb-2 border-bottom text-primary">
                <i className="bi bi-file-pdf me-2"></i>
                {t("pdfImages") || "PDF Görselleri"}
              </h5>
              <Card className="p-3 bg-light">
                <Row className="g-4">
                  <Col xs="12" lg="6">
                    <ImageUploadField
                      label="Pdf Kapak"
                      imageState={pdfCoverImage.imageState}
                      existingImageName={application?.info?.pdfCoverImageName}
                      onFileSelect={pdfCoverImage.handleFileSelect}
                      onDelete={() =>
                        handleDeleteImage("PdfCover", application?.info?.pdfCoverImageName, pdfCoverImage)
                      }
                      dimensions={IMAGE_DIMENSIONS.pdfCover}
                      maxFileSize={MAX_FILE_SIZE}
                      accept={ACCEPTED_IMAGE_TYPES}
                    />
                  </Col>
                  <Col xs="12" lg="6">
                    <ImageUploadField
                      label="Pdf Bilgi"
                      imageState={pdfInfoImage.imageState}
                      existingImageName={application?.info?.pdfInfoImageName}
                      onFileSelect={pdfInfoImage.handleFileSelect}
                      onDelete={() => handleDeleteImage("PdfInfoPage", application?.info?.pdfInfoImageName, pdfInfoImage)}
                      dimensions={IMAGE_DIMENSIONS.pdfInfo}
                      maxFileSize={MAX_FILE_SIZE}
                      accept={ACCEPTED_IMAGE_TYPES}
                    />
                  </Col>
                </Row>
              </Card>
            </div>

            {/* Application Info Section */}
            <div className="mb-5">
              <h5 className="mb-3 pb-2 border-bottom text-primary">
                <i className="bi bi-info-circle me-2"></i>
                {t("applicationInformation") || "Uygulama Bilgileri"}
              </h5>
              <Card className="p-4 bg-light">
                <Row className="g-3">
                  <Col xs="12">
                    <Form.Group>
                      <Form.Label className="fw-semibold">{t("InfoPageDescriptionInputLabel")}</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder={t("enterDescription") || "Açıklama giriniz"}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                      <Form.Text className="text-muted">
                        {t("descriptionHelpText") || "Bu açıklama uygulamanın genel tanımı için kullanılır"}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col xs="12">
                    <Form.Group>
                      <Form.Label className="fw-semibold">{t("answeringPageInfoInputLabel")}</Form.Label>
                      <Form.Control
                        placeholder={t("enterInfoPageTitle") || "Bilgilendirme sayfası başlığı giriniz"}
                        value={infoTitle}
                        onChange={(e) => setInfoTitle(e.target.value)}
                      />
                      <Form.Text className="text-muted">
                        {t("infoTitleHelpText") || "Katılımcılara gösterilecek bilgilendirme sayfası başlığı"}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </Card>
            </div>

            {/* Rich Text Editor Section */}
            <div className="mb-4">
              <h5 className="mb-3 pb-2 border-bottom text-primary">
                <i className="bi bi-pencil-square me-2"></i>
                {t("informationPageContent") || "Bilgilendirme Sayfası İçeriği"}
              </h5>
              <Card className="p-3 bg-light">
                {editorState && (
                  <div className="border rounded bg-white">
                    <Editor editorState={editorState} onEditorStateChange={handleEditorChange} />
                  </div>
                )}
              </Card>
            </div>
          </Container>
        </ScrollableCard>
      </Card>
    </div>
  );
}
